/**
 * FeatureGateContext — React Context für plan-aware Feature-Gating.
 *
 * Verdrahtet den feature-gate.service und stellt Feature-Status
 * per Hook in der gesamten Komponentenhierarchie bereit.
 * Realtime-Subscription auf `practices` für Live-Updates bei Toggle-Änderungen.
 */
import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
    type ReactNode,
} from "react";
import { supabase } from "@/services/supabase-client.service";
import { AuthContext } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";
import { getFeatureGate } from "@/services/feature-gate.service";
import type {
    PracticeFeatureKey,
    FeatureStatus,
    FeatureStatusMap,
    SubscriptionPlan,
} from "@/types/practice";

const LOG = "[FeatureGateContext]";

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface FeatureGateContextType {
    statusMap: FeatureStatusMap | null;
    plan: SubscriptionPlan;
    loading: boolean;
    error: string | null;
    isFeatureEnabled: (key: PracticeFeatureKey) => boolean;
    refreshFeatures: () => Promise<void>;
}

const DISABLED_FALLBACK: FeatureStatus = {
    enabled: false,
    inPlan: false,
    configurable: false,
};

// ═══════════════════════════════════════════════════════════════════════════
// Context
// ═══════════════════════════════════════════════════════════════════════════

const FeatureGateContext = createContext<FeatureGateContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// Provider
// ═══════════════════════════════════════════════════════════════════════════

export function FeatureGateProvider({ children }: { children: ReactNode }) {
    const auth = useContext(AuthContext);
    const practiceId = auth?.practiceId ?? null;
    const [statusMap, setStatusMap] = useState<FeatureStatusMap | null>(null);
    const [plan, setPlan] = useState<SubscriptionPlan>("none");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useRef(true);

    const loadFeatures = useCallback(
        async (id: string) => {
            try {
                setLoading(true);
                setError(null);
                const data = await getFeatureGate(id);
                if (mountedRef.current) {
                    setStatusMap(data.statusMap);
                    setPlan(data.plan);
                }
            } catch (err) {
                logger.error(LOG, "Feature-Gate laden fehlgeschlagen");
                if (mountedRef.current) {
                    setError("Feature-Konfiguration konnte nicht geladen werden.");
                }
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        },
        [],
    );

    const refreshFeatures = useCallback(async () => {
        if (practiceId) {
            await loadFeatures(practiceId);
        }
    }, [practiceId, loadFeatures]);

    // Initial load wenn practiceId verfügbar
    useEffect(() => {
        mountedRef.current = true;

        if (practiceId) {
            loadFeatures(practiceId);
        } else {
            setStatusMap(null);
            setPlan("none");
            setLoading(false);
        }

        return () => {
            mountedRef.current = false;
        };
    }, [practiceId, loadFeatures]);

    // Realtime-Subscription auf practices Tabelle für Live-Updates
    useEffect(() => {
        if (!practiceId) return;

        const channel = supabase
            .channel(`feature-gate:${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "practices",
                    filter: `id=eq.${practiceId}`,
                },
                () => {
                    // Bei Änderung Feature-State neu laden
                    if (mountedRef.current) {
                        loadFeatures(practiceId);
                    }
                },
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [practiceId, loadFeatures]);

    const isFeatureEnabled = useCallback(
        (key: PracticeFeatureKey): boolean => {
            if (!statusMap) return false;
            return statusMap[key]?.enabled ?? false;
        },
        [statusMap],
    );

    return (
        <FeatureGateContext.Provider
            value={{ statusMap, plan, loading, error, isFeatureEnabled, refreshFeatures }}
        >
            {children}
        </FeatureGateContext.Provider>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Hooks
// ═══════════════════════════════════════════════════════════════════════════

/** Voller Context-Zugriff */
export function useFeatureGateContext(): FeatureGateContextType {
    const ctx = useContext(FeatureGateContext);
    if (!ctx) {
        throw new Error("useFeatureGateContext muss innerhalb von FeatureGateProvider verwendet werden.");
    }
    return ctx;
}

/** Status eines einzelnen Features abfragen */
export function useFeatureGate(key: PracticeFeatureKey): FeatureStatus {
    const ctx = useContext(FeatureGateContext);
    if (!ctx || !ctx.statusMap) return DISABLED_FALLBACK;
    return ctx.statusMap[key] ?? DISABLED_FALLBACK;
}
